import { Box, Tab, Tabs } from "@mui/material";
import { useState } from "react";
import { useIntl } from "react-intl";
import { Button, Col } from "reactstrap";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";
import { AssistanceDTO } from "../../models/Assistance/AssistanceDTO";
import AditionalInfo from "../AditionalInfo/AditionalInfo";
import { FilesList } from "../Files/FilesList";
import { useMobile } from "../Layout/UseResponsive";
import AssistanceProfitabilityTable from "../Profitability/AssistanceProfitabilityTable";
import StatusHistoryTable from "../StatusHistory/StatusHistoryTable";
import useDynamicComponent from "../dynamics/useDynamicComponent";
import { AssistanceAdditionalInfoComponent } from "./AssistanceAdditionalInfoComponent";
import { AssistanceCanonSummaryComponent } from "./AssistanceCanonSummaryComponent";
import AssistanceCheckListComponent from "./AssistanceCheckListComponent";
import AssistanceClientQuotations from "./AssistanceClientQuotations";
import { AssistanceDescriptionComponent } from './AssistanceDescriptionComponent';
import { AssistanceExecutionsComponent } from "./AssistanceExecutionsComponent";
import { AssistanceLoanEquipment } from "./AssistanceLoanEquipment";
import { AssistanceLocationComponent } from "./AssistanceLocationComponent";
import { AssistancePartsComponent } from "./AssistancePartsComponent";
import AssistanceRecallComponent from "./AssistanceRecallComponent";
import AssistanceServiceTripsComponent from "./AssistanceServiceTripsComponent";
import AssistanceWarrantyComponent from "./AssistanceWarrantyComponent";

type AssistanceNavBarType = {
    mode: "workshop" | "backoffice";
    assistance: AssistanceDTO;
    setAssistance: React.Dispatch<React.SetStateAction<AssistanceDTO>>;
    isLoadingAssistance: boolean;
    getAssistance: () => Promise<void>;
    reloadExecutions: boolean;
    childrenColumnSize?: number;
    onFetchExecutionsData: () => void;
};

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
    childrenColumnSize: number;
}

function TabPanel({ children, value, index, childrenColumnSize, ...other }: TabPanelProps) {
    return (
        <Col xl={childrenColumnSize}>
            <div
                role="tabpanel"
                hidden={value !== index}
                id={`simple-tabpanel-${index}`}
                aria-labelledby={`simple-tab-${index}`}
                {...other}
            >
                {value === index && <>{children}</>}
            </div>
        </Col>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

export function AssistanceNavBar({
    mode,
    assistance,
    setAssistance,
    isLoadingAssistance,
    getAssistance,
    reloadExecutions,
    childrenColumnSize = 12,
    ...props
}: AssistanceNavBarType) {
    const AssistanceDetails = useDynamicComponent("AssistanceDetails", {
        assistance,
        setAssistance,
        isLoadingAssistance,
        getAssistance,
    });

    const intl = useIntl();
    const isMobile = useMobile();
    const { availableModules } = useAvailableModulesContext();
    const [activeTab, setActiveTab] = useState<number>(0);
    const isComplete = !assistance.userCanEdit;
    const isAcceptedBudget = assistance.status?.toUpperCase() === "ORÇAMENTO ACEITE";

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const dynamicTabs = [
        ...(mode === "workshop"
            ? [{
                module: "Details",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Details" }),
                component: AssistanceDetails
            }]
            : []),
        {
            module: "Description",
            label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Description" }),
            component: (
                <AssistanceDescriptionComponent
                    assistance={assistance}
                    setAssistance={setAssistance}
                    isLoadingAssistance={isLoadingAssistance}
                />
            )
        },
        {
            module: "Parts",
            label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Parts" }),
            component: (
                <AssistancePartsComponent
                    assistanceId={assistance.id ?? 0}
                    reloadExecutions={reloadExecutions}
                    mode={mode}
                    isComplete={isComplete}
                    isAcceptedBudget={isAcceptedBudget}
                    assistanceStatus={assistance.status ?? ''}
                />
            )
        },
        {
            module: "Executions",
            label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Executions" }),
            component: (
                <AssistanceExecutionsComponent
                    assistanceId={assistance.id ?? 0}
                    reloadExecutions={reloadExecutions}
                    mode={mode}
                    isComplete={isComplete}
                    onFetchExecutionsData={props.onFetchExecutionsData}
                    equipmentId={assistance.equipment?.id}
                />
            )
        },
        {
            module: "Images",
            label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Images" }),
            component: (
                <FilesList assistanceId={assistance.id ?? 0} reloadExecutions={reloadExecutions} mode={mode} />
            )
        },
        ...(availableModules.includes("IsActiveLocation")
            ? [{
                module: "Location",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Location" }),
                component: (
                    <AssistanceLocationComponent assistance={assistance} />
                )
            }]
            : []
        ),

        ...(availableModules.includes("IsActiveCanonSummaryModule")
            ? [{
                module: "CanonSummary",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.CanonSummary" }),
                component: (
                    <AssistanceCanonSummaryComponent
                        assistance={assistance}
                        setAssistance={setAssistance}
                        isLoadingAssistance={isLoadingAssistance}
                    />
                )
            }]
            : []),

        ...(availableModules.includes("IsActiveLoanEquipmentModule")
            ? [{
                module: "LoanEquipment",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.LoanEquipment" }),
                component: (
                    <AssistanceLoanEquipment
                        assistance={assistance}
                        isComplete={isComplete}
                    />
                )
            }]
            : []),

        ...(availableModules.includes("IsActiveWarrantyModule")
            ? [{
                module: "Warranty",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Warranty" }),
                component: (
                    <AssistanceWarrantyComponent
                        assistance={assistance}
                        equipmentId={assistance.equipment?.id ?? 0}
                        isComplete={isComplete}
                    />
                )
            }]
            : []),

        ...(availableModules.includes("IsActiveRecallModule")
            ? [{
                module: "Recall",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Recall" }),
                component: (
                    <AssistanceRecallComponent
                        assistance={assistance}
                        equipmentId={assistance.equipment?.id ?? 0}
                        isComplete={isComplete}
                    />
                )
            }]
            : []),
        ...(availableModules.includes("IsActiveAssistanceCheckListModule")
            ? [{
                module: "CheckList",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.CheckList" }),
                component: (
                    <AssistanceCheckListComponent
                        assistance={assistance}
                        setAssistance={setAssistance}
                        loading={isLoadingAssistance}
                    />
                )
            }]
            : []),

        ...(availableModules.includes("IsActiveAditionalInfoModule")
            ? [{
                module: "AditionalInfo",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.AditionalInfo" }),
                component: (
                    <AssistanceAdditionalInfoComponent
                        assistance={assistance}
                        setAssistance={setAssistance}
                        isLoadingAssistance={isLoadingAssistance}
                    />
                )
            }]
            : []),

        ...(availableModules.includes("ShowExternalAssistances")
            ? [{
                module: "Others",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Others" }),
                component: (
                    <AditionalInfo
                        assistanceId={assistance.id ?? 0}
                        reloadExecutions={reloadExecutions}
                        mode={mode}
                        assistance={assistance}
                        isComplete={isComplete}
                    />
                )
            }]
            : []),

        ...(availableModules.includes("IsActiveTripsModule")
            ? [{
                module: "Trips",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Trips" }),
                component: (
                    <AssistanceServiceTripsComponent
                        assistanceId={assistance.id ?? 0}
                        totalDebtkmInAssistance={assistance.totalDebtKm}
                    />
                )
            }]
            : []),

        {
            module: "Status",
            label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Status" }),
            component: (
                <StatusHistoryTable assistanceId={assistance.id ?? 0} reloadExecutions={reloadExecutions} mode={mode} />
            )
        },

        ...(availableModules.includes("IsActiveClientQuotation")
            ? [{
                module: "Quotations",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Quotations" }),
                component: (
                    <AssistanceClientQuotations
                        assistanceStatus={assistance.status ?? ""}
                        assistanceId={assistance.id ?? 0}
                        clientId={assistance.client?.id ?? 0}
                        reloadSignal={reloadExecutions}
                        onReloadComplete={() => { }}
                    />
                )
            }]
            : []),

        ...(availableModules.includes("ProfitabilitySage50cIsActiveProfitabilityModule") && mode === "backoffice"
            ? [{
                module: "Profitability",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Profitability" }),
                component: (
                    <AssistanceProfitabilityTable
                        assistanceId={assistance.id ?? 0}
                        reloadExecutions={reloadExecutions}
                        mode={mode}
                    />
                )
            }]
            : [])
    ];

    return (
        <>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                {isMobile ? (
                    <div style={{
                        display: "flex",
                        overflowX: "auto",
                        gap: "8px",
                        padding: "5px 5px 10px 5px",
                        scrollbarWidth: "none",
                        msOverflowStyle: "none"
                    }}>
                        <style>{`
                            div::-webkit-scrollbar {
                                display: none;
                            }
                        `}</style>
                        {dynamicTabs.map((tab, index) => (
                            <Button
                                key={index}
                                onClick={() => setActiveTab(index)}
                                style={{
                                    backgroundColor: activeTab === index ? "var(--button-main-color)" : "white",
                                    color: activeTab === index ? "white" : "#6c757d",
                                    border: activeTab === index ? "1px solid var(--button-main-color)" : "1px solid #dee2e6",
                                    borderRadius: "20px",
                                    padding: "8px 16px",
                                    whiteSpace: "nowrap",
                                    fontSize: "14px",
                                    fontWeight: activeTab === index ? "500" : "400",
                                    minWidth: "auto",
                                    transition: "all 0.2s ease"
                                }}
                            >
                                {tab.label}
                            </Button>
                        ))}
                    </div>
                ) : (
                    <Tabs
                        value={activeTab}
                        onChange={handleChange}
                        aria-label="assistance tabs"
                        variant="scrollable"
                        scrollButtons
                        allowScrollButtonsMobile
                    >
                        {dynamicTabs.map((tab, index) => (
                            <Tab key={index} label={tab.label} {...a11yProps(index)} />
                        ))}
                    </Tabs>
                )}
            </Box>

            {dynamicTabs.map((tab, index) => (
                <TabPanel key={index} value={activeTab} index={index} childrenColumnSize={childrenColumnSize}>
                    {tab.component}
                </TabPanel>
            ))}
        </>
    );
}
